import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { AssetNotCompliantReason } from '@l2u/shared';
import type { CompanyStatus } from '@l2u/shared/schemas';
import { companyApi } from '@l2u/web-shared';
import { assetName } from './assetName.ts';

/**
 * What the company Dashboard (company/Dashboard.tsx) derives from its two
 * reads, GET /api/me/dashboard and GET /api/me/expiring-documents. Pure, so the
 * wording, links and counting are tested without a DOM.
 */

export type DashboardDriver = { id: string; name: string; status: CompanyStatus };
export type DashboardAsset = {
  id: string;
  registration_plate: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  submitted: boolean;
  reason: AssetNotCompliantReason;
};
export type CompanyDashboardResponse = {
  drivers: {
    total: number; compliant: number; lapsed: number; changes: number; review: number; not_finished: number;
    not_compliant: DashboardDriver[];
  };
  assets: { total: number; compliant: number; not_compliant: DashboardAsset[] };
};
export type ExpiringItem = {
  id: string;
  document_label: string;
  file_name: string;
  expires_at: string;
  days_remaining: number;
  owner_kind: 'company' | 'driver' | 'asset';
  owner_id: string;
  owner_name: string | null;
  asset: { registration_plate: string | null; make: string | null; model: string | null; year: number | null } | null;
};
export type ExpiringDocumentsResponse = { horizon_days: number; expired: ExpiringItem[]; expiring: ExpiringItem[] };
export type AssetComplianceResponse = {
  compliance_status: 'compliant' | 'not_compliant';
  not_compliant_reason: AssetNotCompliantReason | null;
};

export const EXPIRY_WINDOWS = [7, 14, 30, 60, 90] as const;
export const DEFAULT_EXPIRY_WINDOW = 30 as const;

export function parseExpiryWindow(raw: string | null): number {
  const n = Number(raw);
  return (EXPIRY_WINDOWS as readonly number[]).includes(n) ? n : DEFAULT_EXPIRY_WINDOW;
}

// The Dashboard's two reads, defined once: the page (company/Dashboard.tsx)
// and the shell's prefetch (company/CompanyShell.tsx) both use these, so their
// keys and fetchers cannot drift apart.
//
// Both refetch on every visit: no write path (archive an asset, deactivate a
// driver, a renewal) invalidates these keys.
export function companyDashboardQuery() {
  return queryOptions({
    queryKey: ['me', 'dashboard'],
    queryFn: ({ signal }) =>
      companyApi.get<CompanyDashboardResponse>('/api/me/dashboard', { signal }),
    refetchOnMount: 'always',
  });
}

export function expiringDocumentsQuery(days: number) {
  return queryOptions({
    queryKey: ['me', 'expiring-documents', days],
    queryFn: ({ signal }) =>
      companyApi.get<ExpiringDocumentsResponse>(`/api/me/expiring-documents?days=${days}`, {
        signal,
      }),
    refetchOnMount: 'always',
    // A new window keeps the old rows until its own arrive; the first load
    // has no previous data, so it still shows the skeleton.
    placeholderData: keepPreviousData,
  });
}

export type Segment = { key: string; label: string; value: number; color: string };

// Same palette as the admin Dashboard's health bars.
const GREEN = '#16A34A';
const RED = '#DC2626';
const AMBER = '#D97706';
const BLUE = '#2563EB';
const SLATE = '#94A3B8';

export function driverSegments(d: CompanyDashboardResponse['drivers']): Segment[] {
  return [
    { key: 'compliant', label: 'Compliant', value: d.compliant, color: GREEN },
    { key: 'lapsed', label: 'Document lapsed', value: d.lapsed, color: RED },
    { key: 'changes', label: 'Changes requested', value: d.changes, color: AMBER },
    { key: 'review', label: 'Awaiting review', value: d.review, color: BLUE },
    { key: 'not_finished', label: 'Not finished', value: d.not_finished, color: SLATE },
  ];
}

export function assetSegments(a: CompanyDashboardResponse['assets']): Segment[] {
  return [
    { key: 'compliant', label: 'Compliant', value: a.compliant, color: GREEN },
    { key: 'not_compliant', label: 'Not compliant', value: a.total - a.compliant, color: RED },
  ];
}

const REASON_LABEL: Record<AssetNotCompliantReason, string> = {
  rejected: 'Action required',
  unanswered: 'Questions unanswered',
  audit_failed: 'Audit failed',
  policy_lapsed: 'Policy lapsed',
  awaiting_review: 'Awaiting approval',
  nothing_to_judge: 'Nothing to assess',
};

export function assetReasonLabel(reason: AssetNotCompliantReason, submitted: boolean): string {
  return submitted ? REASON_LABEL[reason] : 'Not submitted';
}

export function relativeDays(daysRemaining: number): string {
  if (daysRemaining === 0) return 'today';
  const n = Math.abs(daysRemaining);
  const unit = n === 1 ? 'day' : 'days';
  return daysRemaining > 0 ? `in ${n} ${unit}` : `${n} ${unit} ago`;
}

export function expiringOwnerLink(item: ExpiringItem): `/${string}` {
  if (item.owner_kind === 'driver') return `/drivers/${item.owner_id}`;
  if (item.owner_kind === 'asset') return `/assets/${item.owner_id}`;
  return '/documents';
}

export function expiringOwnerName(item: ExpiringItem, companyLabel: string): string {
  if (item.owner_kind === 'company') return `Your ${companyLabel.toLowerCase()}`;
  if (item.owner_kind === 'asset' && item.asset) return assetName(item.asset);
  return item.owner_name ?? '';
}
